import { Button, IconButton } from '../../components/Button';
import { useSound } from '../../hooks/useSound';
import type { BoardProps } from '../../engine/types';
import { cx } from '../../utils/classnames';
import { VOWELS, allFilled, type BloomMove, type BloomState } from './logic';

export function BloomBoard({ state, status, dispatch, feedback }: BoardProps<BloomState, BloomMove>) {
  const playing = status === 'playing';
  const sound = useSound();
  const nextSlot = state.vowelSlots.find((s) => !state.filled[s]);
  const lastError = feedback?.tone === 'error' ? feedback.nonce : 0;
  const filledComplete = allFilled(state);

  return (
    <div className="cv-board">
      <div className="cv-bloom">
        <div className="cv-bloom__hint">
          Fill in the vowels
          <span className="cv-faint">
            {' '}· {state.maxAttempts - state.attempts} {state.maxAttempts - state.attempts === 1 ? 'try' : 'tries'} left
          </span>
        </div>

        <div key={`row-${lastError}`} className={cx('cv-bloom__row', lastError && 'cv-anim-shake')}>
          {state.pattern.map((c, i) => {
            if (c !== '') {
              return (
                <div key={i} className="cv-tile cv-bloom__given" aria-label={`given ${c}`}>
                  {c.toUpperCase()}
                </div>
              );
            }
            const letter = state.filled[i];
            const active = i === nextSlot && playing;
            const revealed = state.revealed.includes(i);
            return (
              <div
                key={i}
                className={cx('cv-tile cv-bloom__slot', active && 'is-active', revealed && 'is-revealed')}
                data-filled={letter ? 'true' : undefined}
                aria-label={letter ? `vowel ${letter}` : 'empty vowel'}
              >
                {letter ? letter.toUpperCase() : ''}
              </div>
            );
          })}
        </div>

        <div className="cv-bloom__keypad" role="group" aria-label="Vowels">
          {VOWELS.split('').map((v) => (
            <button
              key={v}
              type="button"
              className="cv-bloom__vowel"
              onClick={() => {
                sound.play('key');
                dispatch({ type: 'fill', ch: v });
              }}
              disabled={!playing || nextSlot === undefined}
              aria-label={`Vowel ${v}`}
            >
              {v.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="cv-hintbar">
          <IconButton
            icon="back"
            label="Delete"
            onClick={() => {
              sound.play('delete');
              dispatch({ type: 'remove' });
            }}
            disabled={!playing || !state.vowelSlots.some((s) => state.filled[s] && !state.revealed.includes(s))}
          />
          <Button
            variant="primary"
            onClick={() => {
              sound.play('submit');
              dispatch({ type: 'submit' });
            }}
            disabled={!playing || !filledComplete}
          >
            Enter
          </Button>
        </div>
      </div>

      <div className="cv-board__status" data-tone={feedback?.tone === 'error' ? 'error' : undefined} aria-hidden="true">
        {feedback?.message ?? ''}
      </div>
      <p className="visually-hidden" aria-live="polite">
        {feedback?.message}
      </p>
    </div>
  );
}
